import React, { Component, useCallback, useRef, useState } from "react";

import { faCalendarPlus } from "@fortawesome/pro-regular-svg-icons/faCalendarPlus";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PickerProps } from "antd/es/date-picker/generatePicker";
import Tag from "antd/es/tag";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Deal } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Button, Labeled } from "@mapmycustomers/ui";

import DatePicker from "@app/component/input/DatePicker";
import { dealAnalytics } from "@app/component/preview/analytics";
import { formatDate, formatRawDate } from "@app/util/formatters";
import { parseApiDateWithUtcTz } from "@app/util/parsers";

import AddEventToNylasModal from "./AddEventToNylasModal";
import styles from "./ClosingDateField.module.scss";
import commonStyles from "./CommonField.module.scss";

interface Props {
  deal: Deal;
  disabled?: boolean;
  onChange?: (closingDate: Deal["closingDate"]) => void;
}

const ClosingDateField: React.FC<Props> = ({ deal, disabled, onChange }) => {
  const [addEventModalVisible, showAddEventModal, hideAddEventModal] = useBoolean();

  const intl = useIntl();

  const [date, setDate] = useState<Date | null>(
    deal.closingDate ? parseApiDateWithUtcTz(deal.closingDate) : null
  );

  const [editing, startEditing, cancelEditing] = useBoolean();
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);

  useChangeTracking(() => {
    dealAnalytics.clicked(["Edited Close Date"]);
  }, [date]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        // first esc press closes picker, second pressing cancels editing
        if (pickerOpen) {
          setPickerOpen(false);
        } else {
          cancelEditing();
        }
      }
    },
    [cancelEditing, pickerOpen, setPickerOpen]
  );

  const pickerRef = useRef<Component<PickerProps<Date>, any, any> | null>(null);
  const handleStartEditing = useCallback(() => {
    if (disabled) {
      return;
    }
    setDate(deal.closingDate ? parseApiDateWithUtcTz(deal.closingDate) : new Date());
    startEditing();
    setPickerOpen(true);
    setTimeout(() => {
      // antd picker types are not precise, so converting the type
      if (pickerRef.current) {
        (pickerRef.current as unknown as { focus: () => void }).focus();
      }
    });
  }, [deal.closingDate, disabled, setDate, setPickerOpen, startEditing]);

  const handleSave = useCallback(() => {
    cancelEditing();
    if (onChange) {
      onChange(date ? formatDate(date, "yyyy-MM-dd") : null);
    }
  }, [cancelEditing, date, onChange]);

  return (
    <Labeled
      className={commonStyles.container}
      label={
        <span>
          {intl.formatMessage({
            id: "dealRecord.quickEdit.expectedCloseDate",
            defaultMessage: "Expected Close Date",
            description: "Title of an close date details of a deal when deal is not closed yet",
          })}
          {!disabled && (
            <FontAwesomeIcon
              className={commonStyles.editIcon}
              icon={faPen}
              onClick={handleStartEditing}
              size="xs"
            />
          )}
        </span>
      }
    >
      <div className={styles.dateContainer}>
        <div className={cn(styles.datePicker, { [commonStyles.hidden]: !editing })}>
          <DatePicker
            allowClear
            disabled={disabled}
            format="PP"
            onChange={setDate}
            onKeyDown={handleKeyDown}
            onOpenChange={setPickerOpen}
            open={pickerOpen}
            ref={pickerRef}
            value={date}
          />
          {!disabled && (
            <>
              <Button
                icon={<FontAwesomeIcon icon={faCheck} />}
                iconOnly
                onClick={handleSave}
                type="text"
              />
              <Button
                icon={<FontAwesomeIcon icon={faTimes} />}
                iconOnly
                onClick={cancelEditing}
                type="text"
              />
            </>
          )}
        </div>
        <div
          className={cn(styles.formattedValue, {
            [commonStyles.hidden]: editing,
            [styles.disabled]: disabled,
          })}
          onClick={handleStartEditing}
        >
          <Tag className={styles.tag} color="processing">
            {formatRawDate(deal.closingDate, "PPP", "", parseApiDateWithUtcTz)}
          </Tag>
        </div>
        {!disabled && !editing && (
          <FontAwesomeIcon
            className={styles.addToNylasIcon}
            icon={faCalendarPlus}
            onClick={showAddEventModal}
          />
        )}
      </div>
      {addEventModalVisible && <AddEventToNylasModal deal={deal} onHide={hideAddEventModal} />}
    </Labeled>
  );
};

export default ClosingDateField;
